import React, { useState, useEffect } from 'react';
import { auditAPI, utentiAPI } from '../services/api';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import { ETICHETTE_ACCESSI, etichettaAccesso, etichettaRuolo } from '../utils/etichetteAccessi';
import '../styles/RegistroAccessi.css';

/**
 * RegistroAccessi — schermata riservata agli admin.
 *
 * Risponde a due domande:
 *   "chi ha consultato la cartella di questo paziente?"
 *   "cosa ha consultato questo utente?"
 *
 * La prima serve quando un paziente chiede di sapere chi ha trattato
 * i suoi dati; la seconda per accorgersi di consultazioni anomale.
 *
 * I RIQUADRI FILTRANO (24 settembre 2026, Alex). Un numero di «Ultimi 30
 * giorni» filtra per tipo di accesso, una riga di «Per utente» per utente;
 * i due si combinano, un secondo clic sulla stessa voce toglie il filtro,
 * e i filtri attivi stanno in cima all'elenco con una X. IL FILTRO LO FA
 * IL SERVER: si richiama la rotta coi parametri, non si nascondono righe
 * nel browser - l'elenco e' «le ultime 100 righe che corrispondono», e
 * filtrare cento righe gia' arrivate darebbe un'altra risposta.
 * I numeri dei riquadri NON cambiano filtrando: il riepilogo si legge una
 * volta sola, ed e' il quadro da cui si parte.
 */


const dataOra = (v) =>
    v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/* `nome` si usa SOLO per le righe senza id utente (tablet, utenti tolti):
   per tutti gli altri il filtro e' l'id, piu' il ruolo della riga. */
const FILTRI_VUOTI = { paziente_id: '', utente_id: '', ruolo: '', nome: '', azione: '', dal: '', al: '' };

/* Il valore che il server capisce per «le righe senza utente». */
const SENZA_UTENTE = 'nessuno';

/* La tendina dei tipi: dallo stesso elenco dei riquadri, in ordine di
   etichetta. */
const TIPI = Object.keys(ETICHETTE_ACCESSI)
    .sort((a, b) => ETICHETTE_ACCESSI[a].localeCompare(ETICHETTE_ACCESSI[b], 'it'));

const RegistroAccessi = () => {
    const [accessi, setAccessi] = useState([]);
    const [riepilogo, setRiepilogo] = useState(null);
    const [utenti, setUtenti] = useState([]);
    const [filtri, setFiltri] = useState(FILTRI_VUOTI);
    /* Il nome da scrivere nel filtro attivo quando l'utente l'ha scelto da
       «Per utente»: il filtro lavora sull'id, il nome serve solo a leggerlo. */
    const [nomeFiltroUtente, setNomeFiltroUtente] = useState('');
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);

    const carica = async (f = filtri) => {
        try {
            setCaricamento(true);
            setErrore(null);
            const puliti = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
            /* Riprova da sola una volta se la risposta non arriva (il fermo
               di un rilascio di giorno), poi «Riprova»: qui `errore` e' solo
               della lettura (primaLettura.js). */
            const r = await conUnTentativo(() => auditAPI.accessi(puliti));
            setAccessi(r.data?.data?.accessi || []);
        } catch (e) {
            setErrore(motivoDellaLettura(e, 'Impossibile caricare il registro'));
        } finally {
            setCaricamento(false);
        }
    };

    useEffect(() => {
        carica(FILTRI_VUOTI);
        conUnTentativo(() => auditAPI.riepilogo())
            .then((r) => setRiepilogo(r.data?.data || null))
            .catch(() => {});
        conUnTentativo(() => utentiAPI.getAll())
            .then((r) => setUtenti(r.data?.data?.utenti || []))
            .catch(() => {});
        // eslint-disable-next-line
    }, []);

    const aggiorna = (campo, valore) => setFiltri((p) => ({ ...p, [campo]: valore }));

    /* Cambia i filtri e richiede subito l'elenco al server. */
    const applica = (nuovi) => {
        setFiltri(nuovi);
        carica(nuovi);
    };

    const azzera = () => {
        setNomeFiltroUtente('');
        applica(FILTRI_VUOTI);
    };

    /* --- i riquadri come filtri --- */
    const idDi = (u) => (u.utente_id === null || u.utente_id === undefined ? SENZA_UTENTE : String(u.utente_id));
    const tipoAttivo = (azione) => filtri.azione === azione;
    const nomeDi = (u) => (idDi(u) === SENZA_UTENTE ? (u.utente_nome || '') : '');
    const utenteAttivo = (u) => filtri.utente_id === idDi(u) && filtri.ruolo === (u.utente_ruolo || '')
        && filtri.nome === nomeDi(u);

    const scegliTipo = (azione) => applica({ ...filtri, azione: tipoAttivo(azione) ? '' : azione });

    const scegliUtente = (u) => {
        if (utenteAttivo(u)) {
            setNomeFiltroUtente('');
            applica({ ...filtri, utente_id: '', ruolo: '', nome: '' });
            return;
        }
        setNomeFiltroUtente(`${u.utente_nome || 'non registrato'} · ${etichettaRuolo(u.utente_ruolo)}`);
        applica({ ...filtri, utente_id: idDi(u), ruolo: u.utente_ruolo || '', nome: nomeDi(u) });
    };

    /* Il nome da mostrare per un filtro utente scelto dalla tendina. */
    const nomeUtente = () => {
        if (nomeFiltroUtente) return nomeFiltroUtente;
        if (filtri.utente_id === SENZA_UTENTE) return 'non registrato';
        const u = utenti.find((x) => String(x.id) === String(filtri.utente_id));
        return u ? `${u.cognome} ${u.nome}` : `utente ${filtri.utente_id}`;
    };

    const attivi = [
        filtri.azione && { chiave: 'azione', testo: `Tipo: ${etichettaAccesso(filtri.azione)}`,
                           togli: () => applica({ ...filtri, azione: '' }) },
        filtri.utente_id && { chiave: 'utente', testo: `Utente: ${nomeUtente()}`,
                              togli: () => { setNomeFiltroUtente(''); applica({ ...filtri, utente_id: '', ruolo: '', nome: '' }); } },
    ].filter(Boolean);

    return (
        <div className="ra-wrap">
            <div className="ra-testa">
                <div>
                    <h1>Registro accessi</h1>
                    <p className="ra-sottotitolo">
                        Chi ha consultato quali dati clinici. Le consultazioni ravvicinate
                        dello stesso dato sono raggruppate in finestre di quindici minuti.
                    </p>
                </div>
            </div>

            {riepilogo && (
                <div className="ra-riepilogo">
                    <div className="ra-scheda">
                        <span className="ra-scheda-titolo">Ultimi 30 giorni</span>
                        <div className="ra-conteggi">
                            {riepilogo.per_azione.map((r) => (
                                <button
                                    type="button"
                                    key={r.azione}
                                    className={`ra-conteggio ${tipoAttivo(r.azione) ? 'ra-attivo' : ''}`}
                                    onClick={() => scegliTipo(r.azione)}
                                    aria-pressed={tipoAttivo(r.azione)}
                                    title={tipoAttivo(r.azione) ? 'Clicca di nuovo per togliere il filtro' : 'Mostra solo questo tipo di accesso'}
                                >
                                    <strong>{r.n}</strong>
                                    <span>{etichettaAccesso(r.azione)}</span>
                                </button>
                            ))}
                            {riepilogo.per_azione.length === 0 && (
                                <span className="ra-vuoto-inline">Nessun accesso registrato.</span>
                            )}
                        </div>
                    </div>

                    {riepilogo.per_utente.length > 0 && (
                        <div className="ra-scheda">
                            <span className="ra-scheda-titolo">Per utente</span>
                            <div className="ra-utenti">
                                {riepilogo.per_utente.slice(0, 6).map((u) => (
                                    <button
                                        type="button"
                                        key={`${u.utente_id ?? 'ignoto'}:${u.utente_ruolo || ''}`}
                                        className={`ra-utente ${utenteAttivo(u) ? 'ra-attivo' : ''}`}
                                        onClick={() => scegliUtente(u)}
                                        aria-pressed={utenteAttivo(u)}
                                        title={utenteAttivo(u) ? 'Clicca di nuovo per togliere il filtro' : 'Mostra solo questo utente'}
                                    >
                                        <span className="ra-utente-nome">
                                            {u.utente_nome || 'non registrato'}
                                            <span className="ra-utente-ruolo">{etichettaRuolo(u.utente_ruolo)}</span>
                                        </span>
                                        <span className="ra-utente-dati">
                                            {u.n} accessi · {u.pazienti_distinti} pazienti
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="ra-filtri" data-non-trattiene>
                <div className="ra-campo">
                    <label htmlFor="utente_id">Utente</label>
                    <select
                        id="utente_id"
                        value={filtri.utente_id === SENZA_UTENTE ? '' : filtri.utente_id}
                        onChange={(e) => { setNomeFiltroUtente(''); setFiltri((p) => ({ ...p, utente_id: e.target.value, ruolo: '', nome: '' })); }}
                    >
                        <option value="">tutti</option>
                        {utenti.map((u) => (
                            <option key={u.id} value={u.id}>{u.cognome} {u.nome}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="azione">Tipo di accesso</label>
                    <select
                        id="azione"
                        value={filtri.azione}
                        onChange={(e) => aggiorna('azione', e.target.value)}
                    >
                        <option value="">tutti</option>
                        {TIPI.map((k) => (
                            <option key={k} value={k}>{ETICHETTE_ACCESSI[k]}</option>
                        ))}
                        {filtri.azione && !ETICHETTE_ACCESSI[filtri.azione] && (
                            <option value={filtri.azione}>{etichettaAccesso(filtri.azione)}</option>
                        )}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="paziente_id">Id paziente</label>
                    <input
                        id="paziente_id"
                        type="number"
                        value={filtri.paziente_id}
                        onChange={(e) => aggiorna('paziente_id', e.target.value)}
                        placeholder="es. 11"
                    />
                </div>

                <div className="ra-campo">
                    <label htmlFor="dal">Dal</label>
                    <input id="dal" type="date" value={filtri.dal}
                           onChange={(e) => aggiorna('dal', e.target.value)} />
                </div>

                <div className="ra-campo">
                    <label htmlFor="al">Al</label>
                    <input id="al" type="date" value={filtri.al}
                           onChange={(e) => aggiorna('al', e.target.value)} />
                </div>

                <div className="ra-filtri-azioni">
                    <button className="ra-btn-secondario" onClick={azzera}>Azzera</button>
                    <button className="ra-btn-primario" onClick={() => carica()} disabled={caricamento}>
                        {caricamento ? 'Ricerca…' : 'Cerca'}
                    </button>
                </div>
            </div>

            {attivi.length > 0 && (
                <div className="ra-attivi" aria-label="Filtri attivi">
                    {attivi.map((f) => (
                        <span key={f.chiave} className="ra-filtro-attivo">
                            {f.testo}
                            <button type="button" className="ra-filtro-togli" onClick={f.togli}
                                    aria-label={`Togli il filtro: ${f.testo}`} title="Togli il filtro">
                                ✕
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <ErroreLettura testo={errore} onRiprova={() => carica()} classe="ra-errore" Contenitore="div" />

            {caricamento ? (
                <div className="ra-stato">Caricamento…</div>
            ) : accessi.length === 0 ? (
                <div className="ra-vuoto">Nessun accesso corrisponde ai filtri.</div>
            ) : (
                <div className="rm-scorre">
                <table className="ra-tabella">
                    <thead>
                        <tr>
                            <th>Quando</th>
                            <th>Utente</th>
                            <th>Azione</th>
                            <th>Paziente</th>
                            <th>Dettaglio</th>
                            <th>Provenienza</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accessi.map((a) => (
                            <tr key={a.id} className={a.azione === 'esportazione' ? 'ra-riga-rilievo' : ''}>
                                <td className="ra-quando">{dataOra(a.created_at)}</td>
                                <td>
                                    {a.utente_nome || <span className="ra-ignoto">non registrato</span>}
                                    {a.utente_ruolo && <div className="ra-ruolo">{etichettaRuolo(a.utente_ruolo)}</div>}
                                </td>
                                <td><span className={`ra-azione ra-azione-${a.azione}`} data-azione={a.azione}>{etichettaAccesso(a.azione)}</span></td>
                                <td>
                                    {a.paziente_id ? (
                                        <>
                                            {a.paziente_cognome} {a.paziente_nome}
                                            <div className="ra-pid">{a.id_paziente || `#${a.paziente_id}`}</div>
                                        </>
                                    ) : '—'}
                                </td>
                                <td className="ra-dettaglio">{a.dettaglio || '—'}</td>
                                <td className="ra-ip">{a.ip || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}

            <p className="ra-piede">
                Il registro mostra le ultime 100 righe corrispondenti ai filtri. I dati
                di accesso sono a loro volta informazioni personali e vanno conservati
                per un periodo definito, non indefinitamente.
            </p>
        </div>
    );
};

export default RegistroAccessi;
